import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { UserLogoutRequestEvent } from '@ori-events/user-logout-request';
import { styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { LocalizedText, SkeletonLoader, AccordionMenu, Personalisation } from '../../components';
import { BREAKPOINT_HORIZONTAL_SIDEPANEL, LocalizationItem } from '../../constants';
import { configurationContext } from '../../contexts';
import { useMenuItemsStore, useMenuItemsFilter, clearAllCache } from '../../stores';
import { colors, consoleLog } from '../../utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var Container = styled('div')(function (_ref) {
  var _ref2;
  var theme = _ref.theme;
  return _ref2 = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    boxSizing: 'border-box',
    maxWidth: theme.spacing(66),
    height: 'calc(100vh - var(--top-nav-height))',
    backfaceVisibility: 'initial',
    transition: 'height var(--top-nav-height-animation),background-color 0.1s ease-in-out',
    zIndex: 999,
    '&::WebkitFontSmoothing': 'antialiased',
    '& a': {
      textDecoration: 'none',
      marginLeft: 0
    },
    '& button': {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      height: theme.spacing(10),
      border: 'none',
      fontFamily: 'SansOri, Arial, Helvetica, sans-serif',
      cursor: 'pointer',
      '&:before': {
        marginRight: theme.spacing(3),
        fontSize: '27px'
      }
    }
  }, _defineProperty(_ref2, theme.breakpoints.up(BREAKPOINT_HORIZONTAL_SIDEPANEL), {
    position: 'fixed',
    top: 'var(--top-nav-height)',
    bottom: 0,
    left: 0,
    width: theme.spacing(56),
    maxWidth: '100%',
    backfaceVisibility: 'initial',
    transition: 'unset',
    transitionProperty: 'unset',
    '& a': {
      textDecoration: 'none'
    }
  }), _defineProperty(_ref2, '& ul', {
    margin: 0,
    padding: 0
  }), _ref2;
});
var Scroller = styled('div')(function (_ref3) {
  var theme = _ref3.theme;
  return _defineProperty({
    boxSizing: 'border-box',
    height: 'calc(100vh - var(--top-nav-height))',
    paddingTop: theme.spacing(3),
    overflow: 'hidden auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    MsOverflowStyle: '-ms-autohiding-scrollbar',
    scrollbarWidth: 'thin',
    scrollbarColor: theme.palette.grey[300],
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey[300],
      backgroundClip: 'border-box',
      WebkitBackgroundClip: 'border-box',
      '&:hover': {
        backgroundColor: theme.palette.grey[400]
      },
      '&:active': {
        backgroundColor: theme.palette.blueGrey[200]
      },
      '&:vertical': {
        minHeight: theme.spacing(2)
      },
      '&:horizontal': {
        minWidth: theme.spacing(2)
      }
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent'
    }
  }, theme.breakpoints.down(500), {
    '&::-webkit-scrollbar': {
      width: '3px'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey[300]
    }
  });
});
var Button = styled('button')(function (_ref5) {
  var theme = _ref5.theme;
  return {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    width: '100%',
    height: theme.spacing(10),
    padding: theme.spacing(2, 3),
    overflow: 'hidden',
    background: '#f0f3f0',
    boxShadow: theme.shadows[2],
    color: theme.palette.grey[700],
    fontSize: '15px',
    fontWeight: 'bold',
    letterSpacing: theme.spacing(0.1),
    cursor: 'pointer',
    userSelect: 'none',
    margin: 0,
    '&:before': {
      marginRight: theme.spacing(3),
      fontSize: '27px'
    },
    '&:hover': {
      background: '#e8ece8',
      boxShadow: theme.shadows[4]
    }
  };
});
function doLogout() {
  clearAllCache();
  var userLogoutRequestEvent = new CustomEvent(UserLogoutRequestEvent);
  window.dispatchEvent(userLogoutRequestEvent);
}
export var PersonalMenuSection = function PersonalMenuSection() {
  var isDesktop = useMediaQuery({
    minDeviceWidth: BREAKPOINT_HORIZONTAL_SIDEPANEL
  });
  var _useContext = useContext(configurationContext),
    isDev = _useContext.isDev;
  consoleLog(isDev, {
    text: "".concat(isDesktop),
    color: colors.blue2
  });
  var _useState = useState({
      path: '',
      open: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    menuClickedItem = _useState2[0],
    setMenuClickedItem = _useState2[1];
  var _useMenuItemsStore = useMenuItemsStore(),
    user = _useMenuItemsStore.user,
    items = _useMenuItemsStore.items,
    hasSubitems = _useMenuItemsStore.hasSubitems,
    selectedPath = _useMenuItemsStore.selectedPath;
  var _useMenuItemsFilter = useMenuItemsFilter(items, isDesktop),
    menuItems = _useMenuItemsFilter.filteredItems,
    upgradeToConsultantUrl = _useMenuItemsFilter.upgradeToConsultantUrl,
    upgradeToBrandPartnerUrl = _useMenuItemsFilter.upgradeToBrandPartnerUrl;
  var _useTestId = useTestId(),
    getTestId = _useTestId.getTestId;
  useEffect(function () {
    setMenuClickedItem({
      path: selectedPath,
      open: true
    });
  }, [selectedPath]);
  useEffect(function () {
    if (hasSubitems) {
      var _window$ori, _window$ori$vietnamEL;
      (_window$ori = window.ori) === null || _window$ori === void 0 ? void 0 : (_window$ori$vietnamEL = _window$ori.vietnamELearning) === null || _window$ori$vietnamEL === void 0 ? void 0 : _window$ori$vietnamEL.updateMenuWithVietnam();
    }
  }, [hasSubitems]);
  var handleMenuClick = useCallback(function (event, clickedItem) {
    event.preventDefault();
    setMenuClickedItem(clickedItem);
  }, []);
  if (menuItems.length === 0 && user === null) {
    return /*#__PURE__*/_jsx(SkeletonLoader, {});
  }
  return /*#__PURE__*/_jsxs(Container, {
    "data-testid": getTestId('wrapper'),
    children: [/*#__PURE__*/_jsxs(Scroller, {
      children: [!isDesktop && user ? /*#__PURE__*/_jsx(Personalisation, {
        user: user,
        upgradeToConsultantUrl: upgradeToConsultantUrl,
        upgradeToBrandPartnerUrl: upgradeToBrandPartnerUrl
      }) : null, /*#__PURE__*/_jsx(AccordionMenu, {
        menuItems: menuItems,
        menuClickedItem: menuClickedItem,
        onMenuClick: handleMenuClick
      })]
    }), /*#__PURE__*/_jsx(Button, {
      className: "v-icon-logout",
      type: "button",
      "data-testid": getTestId('logout'),
      onClick: function onClick() {
        return doLogout();
      },
      children: /*#__PURE__*/_jsx(LocalizedText, {
        localizationItem: LocalizationItem.SignOut
      })
    })]
  });
};